import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Stack,
	Button,
	Box,
	IconButton,
	Chip,
} from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { GridColDef, GridColumnHeaderParams } from '@mui/x-data-grid';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useGetUserListQuery } from '@/api/user/userApiSlice';
import { UserFilter, UserPageResDTO } from '@/api/DTO/user/userList.interface';
import UserListSummaryFilterSection from './components/UserListSummaryFilterSection';
import AppleIcon from '@mui/icons-material/Apple';
import FacebookIcon from '@mui/icons-material/Facebook';
import GoogleIcon from '@mui/icons-material/Google';
import AppDataGridHeaderWithSorting from '@/common/components/dataGrid/AppDataGridHeaderWithSorting';
import { showLabel } from '@/utils/helper';

export default function UserListSummaryPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);

	const statusList = [
		{ id: 0, name: t('user.normal') },
		{ id: 1, name: t('user.freeze') },
	];

	const defaultPropertyFilter: UserFilter = {
		pagination: initialPagination,
		searchString: '',
		userStatuses: [],
		sortings: [],
	};
	const defaultValues: DefaultValues<UserFilter> = defaultPropertyFilter;
	const methods = useForm<UserFilter>({
		defaultValues,
	});
	const transformedSearchParams: UserFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'searchString',
			'userStatuses',
			'sortings',
		]),
	};

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetUserListQuery(transformedSearchParams, {
			refetchOnMountOrArgChange: true,
		});

	const transformResponse = useCallback((response?: UserPageResDTO[]) => {
		if (response == null) return;
		const newResponse: any[] | undefined = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				createdAt: !item.createdAt
					? '-'
					: dayjs(item.createdAt)
							.locale('en')
							.format(DISPLAY_DATETIME_12H_FORMAT),
				lastLogin: !item.lastLogin
					? '-'
					: dayjs(item.lastLogin)
							.locale('en')
							.format(DISPLAY_DATETIME_12H_FORMAT),
			});
		});
		return newResponse;
	}, []);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as UserFilter;
			console.log(value);

			// append to url to reload page
			setSearchParams(qs.stringify(propertyFilterFormResult));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	const DataGridHeader = (params: GridColumnHeaderParams) => {
		return (
			<AppDataGridHeaderWithSorting
				headerName={params.colDef.headerName}
				sortingsPropertyName={params.colDef.field}
			/>
		);
	};

	const columns: GridColDef[] = [
		{
			field: 'username',
			headerName: t('user.userName'),
			flex: 15,
			renderHeader: DataGridHeader,
		},
		{
			field: 'nickname',
			headerName: t('user.nickName'),
			flex: 15,
			minWidth: 250,
			renderCell: (params) => (
				<Box>
					<Typography
						sx={{
							display: 'block',
						}}
						fontSize='body2'
						variant='regular'
						color='common.neutral20'
					>
						{params.row.nickname}
					</Typography>
					<Typography
						sx={{
							display: 'block',
						}}
						fontSize='caption'
						color='common.neutral30'
						variant='regular'
					>
						{params.row.phone}
					</Typography>
					<Typography
						sx={{
							display: 'block',
						}}
						fontSize='caption'
						color='common.neutral30'
						variant='regular'
					>
						{params.row.email}
					</Typography>
				</Box>
			),
		},
		{
			field: 'bindOther',
			headerName: t('user.bindOther'),
			flex: 25,
			renderCell: (params) =>
				!params.row.appleId.email &&
				!params.row.facebookId.email &&
				!params.row.googleId.email ? (
					t('user.nobind')
				) : (
					<Box>
						<Typography
							sx={{
								display: 'block',
								overflowWrap: 'break-word',
								wordBreak: 'break-word',
								whiteSpace: 'normal',
							}}
							fontSize='body2'
							variant='regular'
							color='common.neutral20'
						>
							{params.row.appleId.bind && (
								<Box display={'flex'} alignItems={'center'}>
									<AppleIcon sx={{ fontSize: 14 }} />
									{params.row.appleId.email ?? t('user.binded')}
								</Box>
							)}
						</Typography>
						<Typography
							sx={{
								display: 'block',
								overflowWrap: 'break-word',
								wordBreak: 'break-word',
								whiteSpace: 'normal',
							}}
							fontSize='body2'
							variant='regular'
							color='common.neutral20'
						>
							{params.row.facebookId.bind && (
								<Box display={'flex'} alignItems={'center'}>
									<FacebookIcon sx={{ fontSize: 14 }} />
									{params.row.facebookId.email ?? t('user.binded')}
								</Box>
							)}
						</Typography>
						<Typography
							sx={{
								display: 'block',
								overflowWrap: 'break-word',
								wordBreak: 'break-word',
								whiteSpace: 'normal',
							}}
							fontSize='body2'
							variant='regular'
							color='common.neutral20'
						>
							{params.row.googleId.bind && (
								<Box display={'flex'} alignItems={'center'}>
									<GoogleIcon sx={{ fontSize: 14 }} />
									{params.row.googleId.email ?? t('user.binded')}
								</Box>
							)}
						</Typography>
					</Box>
				),
		},
		{
			field: 'createdAt',
			headerName: t('user.registrationTime'),
			flex: 15,
			renderHeader: DataGridHeader,
		},
		{
			field: 'lastLogin',
			headerName: t('user.lastOnlinetime'),
			flex: 15,
			renderHeader: DataGridHeader,
		},
		{
			field: 'userStatus',
			headerName: t('user.userStatus'),
			flex: 10,
			renderCell: (params: any) => (
				<Chip
					color={params.row.userStatus === 0 ? 'chipPrimary' : 'default'}
					label={showLabel(params.row.userStatus, statusList)}
				/>
			),
		},
		{
			field: 'totalMindfulnessTime',
			headerName: t('user.mindfulnessTimeTotal'),
			flex: 10,
		},
		{
			field: 'treasureCount',
			headerName: t('user.treasureTotal'),
			flex: 10,
		},
		{
			field: 'mostExercise',
			headerName: t('user.oftenNav'),
			flex: 21,
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<Link
					to={`/user-manage/user-list/info/${params.id}`}
					state={{
						propertySummarySearchParams: qs.stringify(transformedSearchParams),
					}}
				>
					<IconButton color='primary'>
						<VisibilityOutlinedIcon />
					</IconButton>
				</Link>
			),
		},
	];

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>{t('drawerListItems.userList')}</Typography>
				<Stack direction={'row'} alignItems={'center'} spacing={3}>
					<Button
						variant='contained'
						endIcon={<PersonAddIcon />}
						onClick={() =>
							navigate(`/user-manage/user-list/add`, {
								state: {
									propertySummarySearchParams: qs.stringify(
										transformedSearchParams
									),
								},
							})
						}
						style={{ alignItems: 'center' }}
					>
						{t('button.create')}
					</Button>
				</Stack>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<FormProvider {...methods}>
						<UserListSummaryFilterSection
							statusList={statusList}
						></UserListSummaryFilterSection>

						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.id}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>
		</>
	);
}
